import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const mapSupabaseError = (message) => {
  if (!message) return 'Une erreur est survenue, veuillez réessayer';
  if (message.includes('Invalid login credentials')) return 'Email ou mot de passe incorrect';
  if (message.includes('User already registered')) return 'Cette adresse email est déjà utilisée';
  if (message.includes('Email not confirmed')) return 'Veuillez confirmer votre email avant de vous connecter';
  return 'Une erreur est survenue, veuillez réessayer';
};

export const buildNormalizedUser = async (supabaseUser) => {
  if (!supabaseUser || !supabase) return null;
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('first_name, last_name, full_name, is_premium, onboarding_completed')
    .eq('id', supabaseUser.id)
    .single();
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    full_name: profile?.full_name || '',
    is_premium: profile?.is_premium || false,
    onboarding_completed: profile?.onboarding_completed ?? false,
    created_at: supabaseUser.created_at,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    checkUser();

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        if (session?.user) {
          const normalizedUser = await buildNormalizedUser(session.user);
          setUser(normalizedUser);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
        setLoading(false);
      })();
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const checkUser = async () => {
    if (!supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const normalizedUser = await buildNormalizedUser(session.user);
        setUser(normalizedUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Erreur vérification session:', err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, firstName, lastName) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: `${firstName} ${lastName}`,
          },
        },
      });
      if (signUpError) {
        const msg = mapSupabaseError(signUpError.message);
        setError(msg);
        return { success: false, error: msg };
      }
      return { success: true, message: 'Inscription réussie, vérifiez votre email', userId: data?.user?.id };
    } catch {
      const msg = 'Une erreur est survenue, veuillez réessayer';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    setError('');
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        const msg = mapSupabaseError(signInError.message);
        setError(msg);
        return { success: false, error: msg };
      }
      const normalizedUser = await buildNormalizedUser(data.user);
      setUser(normalizedUser);
      setIsAuthenticated(true);
      return { success: true, userId: data.user.id };
    } catch {
      const msg = 'Une erreur est survenue, veuillez réessayer';
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const normalizedUser = await buildNormalizedUser(session.user);
        setUser(normalizedUser);
      }
    } catch (err) {
      console.error('Erreur refresh user:', err);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setIsAuthenticated(false);
      setError('');
      return { success: true };
    } catch (err) {
      console.error('Erreur déconnexion:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, error, register, login, logout, setError, checkUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
